import React, { useEffect, useState, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CreateCategory } from "../../../RTK/categories/CreateCategories";
import { fetchCategories } from "../../../RTK/categories/GetCategories";
import { X, Upload, Loader2, AlertCircle, EyeOff, Eye } from 'lucide-react';
import { toast } from 'react-toastify';

const CategoryForm = ({ categoryType, onClose }) => {
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const { data, isLoading } = useSelector((state) => state.categories);

  const [formValues, setFormValues] = useState({
    name: "",
    description: "",
    seo_title: "",
    seo_description: "",
    category: "",
    is_hidden: false,
  });

  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (categoryType === "package" || categoryType === "stock") {
      dispatch(fetchCategories("quantity"));
    }
  }, [categoryType, dispatch]);

  const validateForm = () => {
    const errors = {};
    if (!formValues.name.trim()) {
      errors.name = 'الاسم مطلوب';
    }
    if (!formValues.description.trim()) {
      errors.description = 'الوصف مطلوب';
    }
    if (!imageFile) {
      errors.image = 'الصورة مطلوبة';
    }
    if ((categoryType === "package" || categoryType === "stock") && !formValues.category) {
      errors.category = 'الفئة مطلوبة';
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormValues(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleFileChange = (file) => {
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('يرجى اختيار ملف صورة صالح');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        toast.error('يجب أن يكون حجم الصورة أقل من 5 ميجابايت');
        return;
      }

      setImageFile(file);
      if (validationErrors.image) {
        setValidationErrors(prev => ({ ...prev, image: '' }));
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    handleFileChange(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);
    const formData = new FormData();

    if (categoryType === "quantity") {
      formData.append("name", formValues.name.trim());
      formData.append("description", formValues.description.trim());
      formData.append("image", imageFile);
      formData.append("seo_title", formValues.name.trim());
      formData.append("seo_description", formValues.description.trim());
      formData.append("is_hidden", formValues.is_hidden);
    } else if (categoryType === "package") {
      formData.append("name", formValues.name.trim());
      formData.append("description", formValues.description.trim());
      formData.append("banner_image", imageFile);
      formData.append("category", formValues.category);
    } else if (categoryType === "stock") {
      formData.append("product_details", formValues.description.trim());
      formData.append("product_name", formValues.name.trim());
      formData.append("product_image", imageFile);
      formData.append("category", formValues.category);
    }

    try {
      const result = await dispatch(CreateCategory({ formData, categoryType })).unwrap();
      toast.success('تم إنشاء الفئة بنجاح');
      onClose();
    } catch (error) {
      toast.error(error?.message || 'فشل في إنشاء الفئة');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderError = (field) => {
    if (validationErrors[field]) {
      return (
        <div className="flex items-center gap-1 mt-1 text-red-500 text-sm">
          <AlertCircle className="w-4 h-4" />
          <span>{validationErrors[field]}</span>
        </div>
      );
    }
    return null;
  };

  const getCategoryTypeName = () => {
    switch(categoryType) {
      case 'quantity': return 'فئة الكمية';
      case 'package': return 'الباقة';
      case 'stock': return 'عنصر المخزون';
      default: return categoryType;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80">
      <div className="relative bg-gray-900 rounded-xl w-full max-w-2xl overflow-hidden border border-gray-800">
        <div className="absolute right-4 top-4">
          <button
            onClick={onClose}
            className="p-2 rounded-lg bg-gray-800/50 hover:bg-gray-700/50 transition-colors"
          >
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6" dir="rtl">
          <h2 className="text-2xl font-bold text-white mb-6">
            {getCategoryTypeName()} جديد
          </h2>

          <div className="space-y-6">
            {/* Image Upload */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                الصورة
                <span className="text-red-500 mr-1">*</span>
                <span className="text-gray-500 text-xs mr-2">(352 x 192)</span>
              </label>
              <div 
                onClick={() => fileInputRef.current?.click()}
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`mt-1 border-2 border-dashed rounded-lg transition-all cursor-pointer
                  ${validationErrors.image ? 'border-red-500' : isDragging ? 'border-yellow-500 bg-yellow-500/10' : 'border-gray-700 hover:border-yellow-500/50'}`}
              >
                <div className="flex justify-center px-6 pt-5 pb-6">
                  <div className="space-y-1 text-center">
                    {imagePreview ? (
                      <div className="relative group">
                        <img
                          src={imagePreview}
                          alt="معاينة"
                          className="max-h-48 rounded-lg mx-auto"
                        />
                        <div className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg">
                          <p className="text-white text-sm">انقر لتغيير الصورة</p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-col items-center">
                        <Upload className="mx-auto h-12 w-12 text-gray-400" />
                        <p className="mt-2 text-sm text-gray-400">
                          اسحب وأفلت أو انقر للاختيار
                        </p>
                      </div>
                    )}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileChange(e.target.files[0])}
                      className="hidden"
                    />
                  </div>
                </div>
              </div>
              {renderError('image')}
            </div>

            {/* Name */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                الاسم
                <span className="text-red-500 mr-1">*</span>
              </label>
              <input
                type="text"
                name="name"
                value={formValues.name}
                onChange={handleChange}
                className={`w-full bg-gray-800 text-white px-4 py-2.5 rounded-lg border transition-colors
                  ${validationErrors.name ? 'border-red-500' : 'border-gray-700 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500'}`}
                placeholder="أدخل الاسم"
              />
              {renderError('name')}
            </div>

            {/* Description */}
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                الوصف
                <span className="text-red-500 mr-1">*</span>
              </label>
              <textarea
                name="description"
                value={formValues.description}
                onChange={handleChange}
                rows="3"
                className={`w-full bg-gray-800 text-white px-4 py-2.5 rounded-lg border transition-colors resize-none
                  ${validationErrors.description ? 'border-red-500' : 'border-gray-700 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500'}`}
                placeholder="أدخل الوصف"
              />
              {renderError('description')}
            </div>

            {/* Category Selection */}
            {(categoryType === "package" || categoryType === "stock") && (
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  الفئة
                  <span className="text-red-500 mr-1">*</span>
                </label>
                <select
                  name="category"
                  value={formValues.category}
                  onChange={handleChange}
                  className={`w-full bg-gray-800 text-white px-4 py-2.5 rounded-lg border transition-colors
                    ${validationErrors.category ? 'border-red-500' : 'border-gray-700 focus:border-yellow-500 focus:ring-1 focus:ring-yellow-500'}`}
                >
                  <option value="">اختر فئة</option>
                  {Array.isArray(data) && data.map((cat) => (
                    <option key={cat.id} value={cat.id}>
                      {cat.name}
                    </option>
                  ))}
                </select>
                {renderError('category')}
              </div>
            )}

            {/* Visibility Toggle */}
            {categoryType === "quantity" && (
              <div className="flex items-center justify-between bg-gray-800/50 p-4 rounded-lg">
                <div className="flex items-center gap-2">
                  {formValues.is_hidden ? (
                    <EyeOff className="w-5 h-5 text-gray-400" />
                  ) : (
                    <Eye className="w-5 h-5 text-gray-400" />
                  )}
                  <span className="text-gray-300">الظهور</span>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    name="is_hidden"
                    checked={formValues.is_hidden}
                    onChange={handleChange}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-yellow-500 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-yellow-500"></div>
                </label>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="mt-6 flex gap-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2.5 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors font-medium"
            >
              إلغاء
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-yellow-500 text-black rounded-lg hover:bg-yellow-600 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>جاري الإنشاء...</span>
                </>
              ) : (
                <span>إنشاء {getCategoryTypeName()}</span>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoryForm;